import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik, Field, FormikProvider, FieldProps } from 'formik'
import * as yup from 'yup'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchTitleScreens,
  fetchSections,
  selectTitleScreenByClassID,
  selectTitleScreensStatus,
  selectSectionsStatus,
} from 'src/v3/create/create.slice'
import { LeftArrowIcon, RightArrowIcon, AddImageIcon } from 'src/v3/core/icons'
import { ClassUpdated as Class } from 'src/v3/core/types'
import Wysiwyg from 'src/components/common/wysiwyg'
import { processImageFile, uploadImageToS3 } from 'src/v3/utilities/s3'
import noop from 'src/utilities/noop'

interface Props {
  selectedClass?: Class
  setStep: React.Dispatch<
    React.SetStateAction<
      | 'basic-info'
      | 'title-screen'
      | 'content'
      | 'quiz'
      | 'storyboard'
      | 'preview'
    >
  >
}

interface FormValues {
  name: string
  description: string
  content: string
}

export default ({ selectedClass, setStep }: Props): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let titleScreen = useAppSelector(
    selectTitleScreenByClassID(selectedClass?.id || ''),
  )
  let titleScreensStatus = useAppSelector(selectTitleScreensStatus)
  let sectionsStatus = useAppSelector(selectSectionsStatus)

  let [errorMessage, setErrorMessage] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [uploading, setUploading] = useState<boolean>(false)

  let form = useFormik<FormValues>({
    validationSchema: yup.object({
      name: yup.string().required('Title is required'),
      description: yup.string(),
      content: yup.string().required('Content is required'),
    }),
    enableReinitialize: true,
    initialValues: {
      name: titleScreen?.name || selectedClass?.name || '',
      description: titleScreen?.description || selectedClass?.description || '',
      content: titleScreen?.content || '',
    },
    onSubmit: async (values) => {
      if (!selectedClass) {
        setErrorMessage('Class must be created first')
        return
      }

      if (!selectedClass.id) {
        setErrorMessage('Class ID is missing - cannot save title screen')
        return
      }

      setSaving(true)
      try {
        if (!titleScreen?.id) {
          // Create new title screen
          let payload = {
            classID: selectedClass.id,
            name: values.name,
            description: values.description,
            content: values.content,
          }
          await axios.post('/api/v2/create/title-screen', payload)
        } else {
          // Update existing title screen
          await axios.put('/api/v2/create/title-screen', {
            id: titleScreen.id,
            name: values.name,
            description: values.description,
            content: values.content,
          })
        }

        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchTitleScreens(selectedClass.id))
        setStep('content')
      } catch (err: any) {
        setErrorMessage(
          err?.response?.data || err?.message || 'Error saving title screen',
        )
      } finally {
        setSaving(false)
      }
    },
  })

  useEffect(() => {
    if (selectedClass && titleScreensStatus === 'uninitialized') {
      dispatch(fetchTitleScreens(selectedClass.id))
    }
  }, [selectedClass, titleScreensStatus])

  useEffect(() => {
    if (sectionsStatus === 'uninitialized') {
      dispatch(fetchSections())
    }
  }, [sectionsStatus])

  let onImageUpload = (file: File) => {
    if (!selectedClass || !titleScreen?.id) {
      setErrorMessage('Title screen must be saved first')
      return
    }

    setUploading(true)
    axios
      .put('/api/v2/create/title-screen/image', { id: titleScreen.id })
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      .then((response) => uploadImageToS3(file, response.data))
      .then(() => dispatch(fetchTitleScreens(selectedClass.id)))
      .then(() => setUploading(false))
      .catch((err) => {
        setUploading(false)
        setErrorMessage(err?.message || 'Error uploading image')
      })
  }

  if (!selectedClass) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please complete the Basic Info step first to manage the title screen.
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<LeftArrowIcon />}
            onClick={() => setStep('basic-info')}
          >
            Back to Basic Info
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Title Screen
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Set up the introductory screen with hero content and learning
        objectives.
      </Typography>

      <FormikProvider value={form}>
        <form onSubmit={form.handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Title Screen Name"
              name="name"
              value={form.values.name}
              onChange={form.handleChange}
              error={form.touched.name && !!form.errors.name}
              helperText={
                form.touched.name && form.errors.name
                  ? form.errors.name
                  : 'Title displayed to students on the title screen'
              }
              disabled={saving}
            />

            <TextField
              fullWidth
              label="Description"
              name="description"
              multiline
              rows={3}
              value={form.values.description}
              onChange={form.handleChange}
              error={form.touched.description && !!form.errors.description}
              helperText={
                form.touched.description && form.errors.description
                  ? form.errors.description
                  : 'Brief description shown below the title (optional)'
              }
              disabled={saving}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Content *
              </Typography>
              <Field name="content">
                {({ field, meta }: FieldProps) => (
                  <Box>
                    <Wysiwyg
                      value={field.value}
                      onChange={(value: string) =>
                        form.setFieldValue('content', value)
                      }
                      defaultValue="Add learning objectives, course overview, or other introductory content..."
                    />
                    {meta.touched && meta.error && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1, display: 'block' }}
                      >
                        {meta.error}
                      </Typography>
                    )}
                  </Box>
                )}
              </Field>
            </Box>

            {/* Hero Image Upload */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Hero Image
                </Typography>

                {titleScreen?.imageName ? (
                  <Box sx={{ mb: 2 }}>
                    <img
                      src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${titleScreen?.imageName}`}
                      alt="Title screen hero"
                      style={{
                        width: '100%',
                        maxWidth: 400,
                        height: 200,
                        objectFit: 'cover',
                        borderRadius: 8,
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: 200,
                      border: '2px dashed #ccc',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography color="text.secondary">
                      No hero image uploaded
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="outlined"
                  component="label"
                  startIcon={
                    uploading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <AddImageIcon />
                    )
                  }
                  disabled={uploading || !titleScreen?.id}
                >
                  {titleScreen?.imageName
                    ? 'Replace Image'
                    : 'Upload Hero Image'}
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    hidden
                    onChange={(event) =>
                      processImageFile(event, {
                        onLoadingStart: noop,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        onLoadingEnd: onImageUpload,
                      })
                    }
                  />
                </Button>

                {!titleScreen?.id && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    Save the title screen first to upload an image
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}
            >
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/create/v4')}
                  disabled={saving}
                >
                  Save and Exit
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LeftArrowIcon />}
                  onClick={() => setStep('basic-info')}
                  disabled={saving}
                >
                  Back to Basic Info
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  endIcon={
                    saving ? <CircularProgress size={16} /> : <RightArrowIcon />
                  }
                >
                  {saving ? 'Saving...' : 'Save & Continue'}
                </Button>
              </Box>
            </Box>
          </Box>
        </form>
      </FormikProvider>

      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
